import type { PremiseStatusV2 } from '@dte/otw-azure-functions/src/common/interfaces/api/PremiseStatusV2';
import { isOutageCallCode } from '@dte/otw-azure-functions/src/common/premises/data/attributes/powerStatus/isOutageCallCode';
import { PowerIssue } from '@dte/otw-azure-functions/src/report/questions/problemType/PowerIssue';
import { ReportQuestion } from '@dte/otw-azure-functions/src/report/questions/ReportQuestion';
import Image from 'next/image';
import { type PropsWithChildren, type ReactNode, useState } from 'react';
import { InfoAlert } from '../../../../../../../components/Alerts/InfoAlert';
import { WarningAlert } from '../../../../../../../components/Alerts/WarningAlert';
import { Spinner } from '../../../../../../../components/Spinner/Spinner';
import heartHand from '../../../../../../../images/icons/status/heartHand.svg';
import { AuthenticatedJsonContent } from '../../../../content/jsonGet/AuthenticatedJsonContent';
import { usePremiseMasterData } from '../../../../premises/(premiseId)/PremiseMasterDataProvider';
import { getPremiseStatusURL } from '../../../../premises/(premiseId)/status/getPremiseStatusURL';
import { usePremiseId } from '../../../../premises/(premiseId)/usePremiseId';
import { ShowIfAnswerSelected } from '../../HideFromCategory';
import { useIsPoliceFire } from '../safety/useIsPoliceFire';

// TODO: this should be colored in orange for the warning alerts
const HeartHand: ReactNode = (
	<Image src={heartHand} width={24} height={24} alt="Shaking hands forming the shape of a heart" />
);

function FrequentOutageAdvisementsContent(): ReactNode {
	return (
		<InfoAlert title="Frequent Outage Summary" icon={HeartHand}>
			<p>
				I can see you've had several outages this year, and I understand how frustrating that can be. We treat
				situations like yours as a priority so we can address them as quickly as possible.
			</p>
			<p>
				We're committed to improving your reliability and connecting you to the right resources so you can
				receive regular updates.
			</p>
			<p>
				We appreciate your patience, especially knowing this isn't the first time you've experienced an
				interruption this year.
			</p>
			<p>
				This order we created today is meant to address both the current outage and your ongoing service issues.
			</p>
			<p>You'll receive updates as the work progresses.</p>
			<p>We'll keep you informed as we learn more.</p>
		</InfoAlert>
	);
}

/**
 * Content to only show for customers with frequent outages
 * @param props
 */
export function FrequentOutageReportWrapper(props: PropsWithChildren): ReactNode {
	// TODO: add in criteria looking at frequent outage flag
	const policeFire = useIsPoliceFire();
	if (policeFire) {
		return null;
	}

	return (
		<ShowIfAnswerSelected question={ReportQuestion.PowerIssue} answerKey={PowerIssue.Outage}>
			{props.children}
		</ShowIfAnswerSelected>
	);
}

/**
 * Content to only show for premises with frequent outages
 *
 * @param props
 * @returns
 */
export function FrequentOutagePremiseWrapper(props: PropsWithChildren): ReactNode {
	const masterData = usePremiseMasterData();
	if (!masterData?.data?.outageHistory?.frequentOutages) {
		return null;
	}

	return <>{props.children}</>;
}

export function FrequentOutageAdvisements({ callCode }: { callCode?: string }): ReactNode {
	// Don't show police / fire advisements for outages
	const policeFire = useIsPoliceFire();
	if (policeFire) {
		return null;
	}

	// Don't show unless reporting an outage
	const outage = isOutageCallCode(callCode);
	if (!outage) {
		return null;
	}

	return (
		<FrequentOutagePremiseWrapper>
			<FrequentOutageReportWrapper>
				<FrequentOutageAdvisementsContent />
			</FrequentOutageReportWrapper>
		</FrequentOutagePremiseWrapper>
	);
}

interface FrequentOutageWarningProps {
	// Show warning even if there is not currently an open outage
	showIfNoOpenOutage?: boolean;
	// Show warning if there is currently an open outage
	showIfOpenOutage?: boolean;
	status?: PremiseStatusV2;
}

function FrequentOutageWarningContent({
	showIfNoOpenOutage = true,
	showIfOpenOutage = true,
	status,
}: FrequentOutageWarningProps): ReactNode {
	const masterData = usePremiseMasterData();

	// Still loading status
	if (!status) {
		return null;
	}

	const openOutage = status.outage && !status.closeDate;
	if (openOutage) {
		// Should we show if there is an open outage?
		if (!showIfOpenOutage) {
			return null;
		}
	} else {
		// Should we show if there isn't an open outage?
		if (!showIfNoOpenOutage) {
			return null;
		}
	}

	const outageCount = masterData?.data?.outageHistory?.recentOutages ?? <Spinner />;

	const title = (
		<>
			Frequent Outages: <span style={{ fontWeight: 400 }}>{outageCount} this year</span>
		</>
	);

	return (
		<WarningAlert title={title} icon={HeartHand}>
			<b>Tip:</b> Please be more patient and understanding with this customer because of their multiple outages.
		</WarningAlert>
	);
}

export function FrequentOutageWarning(props: Omit<FrequentOutageWarningProps, 'status'>): ReactNode {
	const premiseIdValue = usePremiseId();
	const url = getPremiseStatusURL(premiseIdValue);
	const [status, setStatus] = useState<PremiseStatusV2>();

	return (
		<AuthenticatedJsonContent url={url} setData={setStatus}>
			<FrequentOutagePremiseWrapper>
				<FrequentOutageWarningContent {...props} status={status} />
			</FrequentOutagePremiseWrapper>
		</AuthenticatedJsonContent>
	);
}
